import React, { Fragment, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { requestInform } from 'api/sfms/mix/ratio';
import Select from 'components/common-sfims/form/Select';
import List from 'components/common-sfims/List';
import Modal from 'components/common-sfims/modal/Common';
import mixColumns from 'components/pages/sfms/mix/ratio/columns/inform/mix';
import productColumns from 'components/pages/sfms/mix/ratio/columns/inform/product';
import { datas2ExcelJSON } from 'function/convert';
import { onInputChange } from 'function/event';
import { downloadExcelByJSON } from 'function/file';
import { getText } from 'function/language';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import formatNumber from 'utils/format/number';

/**
 * 배합비 등록 관리 > 배합비 - 확정 배합비
 */
const Inform = ({ show, seq, onHide }) => {
  /* state */
  // 컬럼 목록
  const [columns, setColumns] = useState([]);
  // 목록
  const [datas, setDatas] = useState([]);
  // 목록 끝 번호
  const [end, setEnd] = useState(15);
  // 배합비 정보
  const [inform, setInform] = useObjReducer({});
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // 메시지
  const [message, setMessage] = useState({});
  // 배합비 정보 조회 요청 데이터
  const [params, setParams] = useObjReducer({ type: 'PRODUCT' });
  // 총 데이터 수
  const [total, setTotal] = useState(0);

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우 데이터 초기화
    if (show) {
      setParams({ type: 'PRODUCT' });
    }
  }, [show]);
  useEffect(() => {
    // 모달이 표시된 경우
    if (!show) {
      return;
    }

    const columns = {
      PRODUCT: productColumns,
      MIX: mixColumns
    };

    // 컬럼 목록 설정
    setColumns(columns[params.type]);

    // 배합비 정보 조회 요청
    requestList();
  }, [show, params.type]);

  /* method */
  // 배합비 정보 조회 요청
  const requestList = async () => {
    setDatas([]);
    setEnd(15);
    setIsLoading(true);
    setMessage({});
    setTotal(0);

    try {
      const { data = {}, code, message } = await requestInform(seq, params);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      const { mixRatioInfoSubList = [] } = data;
      const { length } = mixRatioInfoSubList;

      setDatas(
        mixRatioInfoSubList.map((value = {}, index) => ({
          ...value,
          rowNum: length - index
        }))
      );
      setInform(data);
      setTotal(length);
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  /* event */
  // 엑셀 다운로드 클릭 시
  const onDownloadExcelClick = () => {
    downloadExcelByJSON(
      datas2ExcelJSON(datas, columns),
      [getText('mixRt'), inform.mixNm].join('_').concat('.xlsx')
    );
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{ footer: 'align-items-center justify-content-center' }}
      size="xl"
      onHide={onHide}
    >
      <Fragment key="header">{getText('mixRt')}</Fragment>
      <Fragment key="body">
        <Select
          datas={[
            {
              text: 'byProduct',
              value: 'PRODUCT'
            },
            {
              text: 'byMix',
              value: 'MIX'
            }
          ]}
          value={params.type}
          className="w-25 mb-3"
          name="type"
          onChange={event => onInputChange(event, setParams)}
        />
        <div id="scroll" className="max-vh-50 scrollbar">
          <InfiniteScroll
            hasMore={end < total}
            dataLength={datas.slice(0, end).length}
            scrollableTarget="scroll"
            next={() => setEnd(end + 15)}
          >
            <List
              className={{ table: 'mb-0' }}
              columns={Object.entries(columns).map(
                ([key, { title } = {}]) => title ?? key
              )}
              message={message}
              showLoading={isLoading}
            >
              {datas.length > 0 && (
                <>
                  {datas.slice(0, end).map((value = {}) => (
                    <tr key={`datas${value.rowNum}`}>
                      {Object.entries(columns).map(
                        ([key, { type, format } = {}]) => (
                          <td
                            key={`datas${value.rowNum}.${key}`}
                            className={classNames({
                              'text-end': type === 'number'
                            })}
                          >
                            {type === 'number'
                              ? formatNumber(value[key], format)
                              : value[key]}
                          </td>
                        )
                      )}
                    </tr>
                  ))}
                  <tr className="bg-soft-warning fw-bold">
                    <td />
                    <td>{inform.mixCd}</td>
                    <td>{inform.mixNm}</td>
                    <td />
                    <td />
                    <td className="text-end">
                      {formatNumber(inform.mixRt, ',0.000')}
                    </td>
                  </tr>
                </>
              )}
            </List>
          </InfiniteScroll>
        </div>
      </Fragment>
      <Fragment key="footer">
        {datas.length > 0 && (
          <Button className="ms-2" onClick={onDownloadExcelClick}>
            {getText('downloadExcel')}
          </Button>
        )}
        <Button variant="secondary" onClick={onHide}>
          {getText('close')}
        </Button>
      </Fragment>
    </Modal>
  );
};

Inform.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 배합비 번호
  seq: PropTypes.number.isRequired,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func
};
Inform.defaultProps = { show: false };

export default Inform;
